import { Subscribable, SubscribableOrNullableValue, SubscribableOrValue } from "knockout";
import WCCSliderItem from "../item/base/base.model";
import WCCSliderDynamicItem from "../item/dynamic/dynamic.model";
import WCCSliderPage from "../models/page";
import WCCSliderBaseLayout, { WCCSliderBaseLayoutConfig } from "./base";

export interface WCCSliderDynamicLayoutContext<T> {
    page: WCCSliderPage<T, WCCSliderDynamicItem<T>>
    itemWidth: number
    columnsCount: number
}

export interface WCCSliderDynamicLayoutConfig {
    minWidth: SubscribableOrValue<number>
    maxWidth: SubscribableOrValue<number>
    offset?: SubscribableOrNullableValue<number>
}

export interface WCCSliderDynamicLayoutConfigFull<T> extends WCCSliderBaseLayoutConfig<T, WCCSliderDynamicItem<T>>, WCCSliderDynamicLayoutConfig { }

export default class WCCSliderDynamicLayout<T> extends WCCSliderBaseLayout<T, WCCSliderDynamicItem<T>, WCCSliderDynamicLayoutContext<T>> {
    minWidth: Subscribable<number>
    maxWidth: Subscribable<number>
    offset: Subscribable<number>

    itemWidth: Subscribable<number>

    constructor(config: WCCSliderDynamicLayoutConfigFull<T>) {
        super(config);

        this.minWidth = ko.flattenComputed(config.minWidth);
        this.maxWidth = ko.flattenComputed(config.maxWidth);
        this.offset = ko.flattenComputed(config.offset, 0);

        const itemsConfig = ko.pureComputed(() => this.getItemsConfig(this.width()));

        this.itemWidth = itemsConfig.pluck(config => config.itemWidth);

        queueMicrotask(() => {
            this.effects.register([
                ko.computed(() => this.updatePages()).extend({ deferred: true }),
                ko.computed(() => this.updateBorrowedItems()).extend({ deferred: true })
            ]);
        })
    }

    protected buildItem(data: T): WCCSliderDynamicItem<T> {
        return new WCCSliderDynamicItem({ data });
    }

    protected canAddItem(item: WCCSliderItem<T>, { page, columnsCount }: WCCSliderDynamicLayoutContext<T>): boolean {
        return page.allItems().length < columnsCount;
    }    

    protected getContext(pages: WCCSliderPage<T, WCCSliderDynamicItem<T>>[], page: WCCSliderPage<T, WCCSliderDynamicItem<T>>, isFinal = false) {
        let config = this.getItemsConfig(this.getPageWidth({
            isFirst: page === pages[0],
            isLast: isFinal && page === pages[pages.length - 1]
        }));

        return {
            page: page,
            itemWidth: config.itemWidth,
            columnsCount: config.columnsCount
        };
    }

    private updatePages() {
        const offset = this.offset();

        this.pages().forEach((page, idx, pages) => {
            const context = this.getContext(pages, page, true);
            const itemWidth = context.itemWidth;

            page.items().forEach((item, idx) => {
                item.borrowedWidth(0);

                if (idx > 0 || this.areOtherPagesShown()) {
                    item.width(itemWidth + offset);
                    item.paddingLeft(offset);
                } else {
                    item.width(itemWidth);
                    item.paddingLeft(0);
                }
            });

            page.borrowedItems().forEach(borrowedItem => borrowedItem.borrowedWidth(itemWidth));
        });
    }

    private updateBorrowedItems() {
        var activePage = this.activePage();

        this.pages().forEach(page => {
            page.items().forEach(item => item.borrowed(false));

            if (page === activePage)
                page.borrowedItems().forEach(item => item.borrowed(true));
        });
    }

    private getItemsConfig(pageWidth: number) {
        const minWidth = this.minWidth();
        const maxWidth = this.maxWidth();
        const offset = this.offset();

        if (pageWidth < minWidth)
            return {
                itemWidth: pageWidth,
                columnsCount: 1
            };

        let columnsCount = Math.ceil(pageWidth / maxWidth),
            itemWidth = Math.floor(pageWidth / columnsCount);

        if (itemWidth < minWidth) {
            columnsCount = Math.floor(pageWidth / minWidth);
            itemWidth = Math.floor(pageWidth / columnsCount);
        }

        if (offset > 0)
            itemWidth = Math.floor((pageWidth - offset * (columnsCount - 1)) / columnsCount);

        return {
            itemWidth: itemWidth,
            columnsCount: columnsCount
        }
    }
}